<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import ProgressBar from 'primevue/progressbar'
import Drawer from 'primevue/drawer'
import Tag from 'primevue/tag'
import { useTablesStore } from '~/stores/admin/tables'
import { useDiningAreasStore } from '~/stores/admin/diningAreas'
import type { Table } from '~/types/tables'

const tablesStore = useTablesStore()
const diningAreasStore = useDiningAreasStore()
const posCheckStore = usePosCheckStore()
const checksSyncStore = useChecksSyncStore()
const { filteredTables, loading } = storeToRefs(tablesStore)
// Occupancy reads from the POS single source of truth (checksSync), same as openTablelessChecks
const { checks } = storeToRefs(checksSyncStore)
const { selectedTableId, openTablelessChecks } = storeToRefs(posCheckStore)
const { t } = useNuxtApp().$i18n

// Loading state for table selection
const selectingTable = ref(false)

// New Check Drawer state
const newCheckDrawer = ref(false)
const newCheckType = ref<'togo' | 'delivery' | 'custom'>('togo')
const newCheckSelectedCustomer = ref<any>(null)
const customerPickerRef = ref<any>(null)
const customLabel = ref('')
const currentStep = ref(1)
const customerEditMode = ref(false)

const checkTypeOptions = [
  { label: 'To-Go', value: 'togo' as const, icon: 'pi pi-shopping-bag' },
  { label: 'Delivery', value: 'delivery' as const, icon: 'pi pi-truck' },
  { label: 'Custom', value: 'custom' as const, icon: 'pi pi-tag' },
]

const log = useLogger('component:TablesList')

// Fetch data on mount.
// allSettled y no all: si una de las tres muere —red caída, o la página
// descargándose mientras vuelan— una promesa rechazada aquí llega al manejador
// de errores de Vue y renderiza la pantalla de error encima del POS. El cajero
// prefiere un panel incompleto a perder la caja en medio del servicio.
onMounted(async () => {
  const results = await Promise.allSettled([
    tablesStore.fetchTables(),
    diningAreasStore.fetchDiningAreas(),
    checksSyncStore.fetchChecks(),
  ])

  let degraded = false
  for (const result of results) {
    if (result.status === 'rejected') {
      degraded = true
      log.error('mount:fetch', result.reason)
    }
  }

  // Sin aviso, un fetch de cuentas caído pinta todas las mesas como libres y el
  // cajero abre una segunda cuenta sobre una mesa ocupada.
  if (degraded) {
    useErrorToast('The tables board did not load completely, refresh before taking orders')
  }
})

// Get check for a table
function getCheckForTable(tableId: number) {
  return checks.value.find(
    (check: any) => check.table_id === tableId && check.status === 'open',
  )
}

// Extract sequence number from check_number (YYYYMMDD0001 -> 0001)
function getCheckSequence(checkNumber: string): string {
  return checkNumber.slice(-4)
}

// Group tables by dining area
const tablesByArea = computed(() => {
  const grouped: Record<string, Table[]> = {}

  filteredTables.value.forEach((table) => {
    if (table.active) {
      const areaName = table.dining_area?.name || t('Unassigned')
      if (!grouped[areaName]) {
        grouped[areaName] = []
      }
      grouped[areaName].push(table)
    }
  })

  return grouped
})

// Handle table click
async function selectTable(table: Table) {
  selectingTable.value = true
  try {
    await posCheckStore.selectTableWithConfirm(table.id)
  }
  finally {
    setTimeout(() => {
      selectingTable.value = false
    }, 500)
  }
}

// Refresh data
async function refreshData() {
  await Promise.all([
    tablesStore.fetchTables(),
    checksSyncStore.fetchChecks(),
  ])
}

// Get table color classes based on state
function getTableColorClasses(table: Table) {
  const hasCheck = getCheckForTable(table.id)
  const isSelected = selectedTableId.value === table.id

  if (isSelected) return 'bg-gradient-to-r from-green-600 to-green-700 text-white shadow-md animate-pulse'
  if (hasCheck) return 'bg-gradient-to-r from-cyan-600 to-cyan-700 text-white shadow-md'
  return 'bg-slate-700 hover:bg-slate-600 text-white'
}

function selectCheckType(type: 'togo' | 'delivery' | 'custom') {
  newCheckType.value = type
  currentStep.value = 2
  customerEditMode.value = false
}

function goToStep1() {
  currentStep.value = 1
}

function onCustomerSelect(customer: any) {
  newCheckSelectedCustomer.value = customer
  customerEditMode.value = false
}

function onCustomerClear() {
  newCheckSelectedCustomer.value = null
  customerEditMode.value = false
}

async function openTablelessCheck() {
  const success = await posCheckStore.openTablelessCheck({
    check_type: newCheckType.value,
    customer_id: newCheckSelectedCustomer.value?.id || undefined,
    customer_name: newCheckSelectedCustomer.value?.name || undefined,
    customer_phone: newCheckSelectedCustomer.value?.phone || undefined,
    custom_label: customLabel.value || undefined,
  })

  if (success) {
    newCheckDrawer.value = false
    resetDrawerForm()
  }
}

function resetDrawerForm() {
  newCheckType.value = 'togo'
  newCheckSelectedCustomer.value = null
  customLabel.value = ''
  currentStep.value = 1
  customerEditMode.value = false
}

// Tableless check click
async function selectTablelessCheck(checkId: number) {
  selectingTable.value = true
  await posCheckStore.loadExistingCheck(checkId)
  setTimeout(() => {
    selectingTable.value = false
  }, 500)
}

function getCheckTypeSeverity(checkType: string): 'warn' | 'info' | 'secondary' {
  switch (checkType) {
    case 'togo': return 'warn'
    case 'delivery': return 'info'
    default: return 'secondary'
  }
}

function getCheckTypeLabel(checkType: string): string {
  switch (checkType) {
    case 'togo': return t('To-Go')
    case 'delivery': return t('Delivery')
    case 'custom': return t('Custom')
    default: return checkType
  }
}
</script>

<template>
  <div class="h-full bg-white rounded-lg shadow-sm flex flex-col w-full">
    <!-- Header -->
    <div class="px-2 py-0 border-b border-gray-200">
      <div class="flex items-center justify-between mb-0">
        <h3 class="text-sm font-semibold text-gray-700">
          {{ t('Tables') }}
        </h3>
        <div class="flex items-center gap-1">
          <Button
            icon="pi pi-plus"
            class="p-button-rounded p-button-sm"
            :aria-label="t('New Check')"
            severity="success"
            raised
            @click="newCheckDrawer = true"
          />
          <Button
            icon="pi pi-refresh"
            class="p-button-text p-button-rounded p-button-sm"
            :aria-label="t('Refresh')"
            @click="refreshData"
          />
        </div>
      </div>
    </div>

    <!-- Loading ProgressBar -->
    <ProgressBar
      v-if="selectingTable"
      mode="indeterminate"
      class="h-1"
      style="height: 4px;"
    />

    <!-- Loading State: Shimmer Skeleton -->
    <div
      v-if="loading"
      class="flex-1 overflow-y-auto p-2"
    >
      <div class="mb-3">
        <div class="h-4 w-20 bg-gray-200 rounded mb-2 animate-pulse" />
        <div class="grid grid-cols-2 gap-1">
          <div
            v-for="i in 6"
            :key="i"
            class="relative w-full min-w-[120px] rounded-lg min-h-16 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-shimmer"
          />
        </div>
      </div>
    </div>

    <!-- Tables List -->
    <div
      v-else
      class="flex-1 overflow-y-auto p-2"
    >
      <div
        v-for="(areaTables, areaName) in tablesByArea"
        :key="areaName"
        class="mb-3"
      >
        <div class="text-xs font-semibold text-gray-600 mb-1 px-2">
          {{ areaName }}
        </div>
        <div class="grid grid-cols-2 gap-1">
          <button
            v-for="(table, index) in areaTables"
            :key="table.id"
            :class="[
              'relative w-full min-w-[120px] cursor-pointer overflow-hidden rounded-lg min-h-16 transition-all duration-200 p-2 flex flex-col items-center justify-center gap-1 border-0',
              'hover:scale-105 active:scale-95',
              'animate-fadeInUp',
              selectingTable ? 'opacity-50 cursor-wait' : '',
              getTableColorClasses(table),
            ]"
            :style="{ animationDelay: `${index * 50}ms` }"
            :disabled="selectingTable"
            @click="selectTable(table)"
          >
            <!-- Table Number -->
            <span class="text-base font-semibold text-center">
              {{ table.table_number }}
            </span>

            <!-- Check Number (if active) -->
            <span
              v-if="getCheckForTable(table.id)"
              class="text-xs font-medium opacity-90"
            >
              #{{ getCheckSequence(getCheckForTable(table.id)?.check_number || '') }}
            </span>

            <!-- Customer Name (if assigned) -->
            <span
              v-if="getCheckForTable(table.id)?.customer_name"
              class="text-[10px] opacity-75 truncate max-w-full"
            >
              {{ getCheckForTable(table.id)?.customer_name }}
            </span>
          </button>
        </div>
      </div>

      <!-- Open Orders (Tableless Checks) -->
      <div
        v-if="openTablelessChecks.length > 0"
        class="mb-3 mt-2"
      >
        <div class="text-xs font-semibold text-gray-600 mb-1 px-2">
          {{ t('Open Orders') }}
        </div>
        <div class="grid grid-cols-2 gap-1">
          <button
            v-for="check in openTablelessChecks"
            :key="check.id"
            :class="[
              'relative w-full min-w-[120px] cursor-pointer overflow-hidden rounded-lg min-h-16 transition-all duration-200 p-2 flex flex-col items-center justify-center gap-1 border-0',
              'hover:scale-105 active:scale-95',
              posCheckStore.activeCheckId === check.id
                ? 'bg-gradient-to-r from-green-600 to-green-700 text-white shadow-md animate-pulse'
                : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md',
            ]"
            @click="selectTablelessCheck(check.id)"
          >
            <Tag
              :value="getCheckTypeLabel(check.check_type)"
              :severity="getCheckTypeSeverity(check.check_type)"
              class="text-[9px] px-1 py-0"
            />
            <span class="text-sm font-semibold text-center truncate w-full">
              {{ check.custom_label || check.customer_name || t('Walk-in') }}
            </span>
            <span class="text-xs font-medium opacity-90">
              #{{ getCheckSequence(check.check_number) }}
            </span>
          </button>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="Object.keys(tablesByArea).length === 0 && openTablelessChecks.length === 0"
        class="text-center p-4 text-gray-500"
      >
        <i class="pi pi-inbox text-2xl mb-2" />
        <p class="text-sm">
          {{ t('No tables available') }}
        </p>
      </div>
    </div>

    <!-- New Check Drawer -->
    <Drawer
      v-model:visible="newCheckDrawer"
      :header="t('New Check')"
      position="right"
      class="!w-[480px]"
      @hide="resetDrawerForm"
    >
      <div class="flex gap-4">
        <!-- Left: Step Indicators -->
        <div class="flex flex-col items-center w-[80px] shrink-0 pt-1">
          <!-- Step 1 -->
          <button
            class="flex flex-col items-center gap-1 cursor-pointer bg-transparent border-0 p-0"
            @click="goToStep1"
          >
            <div
              :class="[
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors',
                currentStep >= 1 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500',
              ]"
            >
              1
            </div>
            <span
              :class="[
                'text-[10px] font-medium transition-colors',
                currentStep >= 1 ? 'text-green-700' : 'text-gray-400',
              ]"
            >{{ t('Check Type') }}</span>
          </button>
          <!-- Connecting line -->
          <div class="w-px h-8 bg-gray-200 my-1" />
          <!-- Step 2 -->
          <div class="flex flex-col items-center gap-1">
            <div
              :class="[
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors',
                currentStep >= 2 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500',
              ]"
            >
              2
            </div>
            <span
              :class="[
                'text-[10px] font-medium transition-colors',
                currentStep >= 2 ? 'text-green-700' : 'text-gray-400',
              ]"
            >{{ t('Details') }}</span>
          </div>
        </div>

        <!-- Right: Step Content -->
        <div class="flex-1 flex flex-col gap-4">
          <!-- Step 1: Type Selection -->
          <div v-if="currentStep === 1">
            <p class="text-sm text-gray-500 mb-3">
              {{ t('Select check type') }}
            </p>
            <div class="flex flex-col gap-2">
              <button
                v-for="opt in checkTypeOptions"
                :key="opt.value"
                class="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                @click="selectCheckType(opt.value)"
              >
                <div class="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                  <i :class="[opt.icon, 'text-xl text-gray-600']" />
                </div>
                <span class="flex-1 text-sm font-medium text-gray-800 text-left">{{ t(opt.label) }}</span>
                <i class="pi pi-chevron-right text-gray-400 text-sm" />
              </button>
            </div>
          </div>

          <!-- Step 2: Details -->
          <div v-if="currentStep === 2">
            <!-- Back + Type badge -->
            <div class="flex items-center gap-2 mb-4">
              <button
                class="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 border-0 cursor-pointer transition-colors"
                @click="goToStep1"
              >
                <i class="pi pi-arrow-left text-sm text-gray-600" />
              </button>
              <Tag
                :value="getCheckTypeLabel(newCheckType)"
                :severity="getCheckTypeSeverity(newCheckType)"
              />
            </div>

            <!-- Selected customer card -->
            <div
              v-if="newCheckSelectedCustomer"
              class="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200 mb-3"
            >
              <div class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                <i class="pi pi-user text-green-600" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-800 truncate">
                  {{ newCheckSelectedCustomer.name }}
                </p>
                <p
                  v-if="newCheckSelectedCustomer.phone"
                  class="text-xs text-gray-500 truncate"
                >
                  <i class="pi pi-phone text-[10px]" /> {{ newCheckSelectedCustomer.phone }}
                </p>
                <p
                  v-if="newCheckSelectedCustomer.email"
                  class="text-xs text-gray-500 truncate"
                >
                  <i class="pi pi-envelope text-[10px]" /> {{ newCheckSelectedCustomer.email }}
                </p>
                <p
                  v-if="newCheckSelectedCustomer.address_line_1"
                  class="text-xs text-gray-500 truncate"
                >
                  <i class="pi pi-map-marker text-[10px]" /> {{ newCheckSelectedCustomer.address_line_1 }}
                </p>
                <p
                  v-if="newCheckSelectedCustomer.comments"
                  class="text-xs text-gray-400 truncate italic"
                >
                  {{ newCheckSelectedCustomer.comments }}
                </p>
              </div>
              <div class="flex items-center gap-1 shrink-0">
                <button
                  class="w-8 h-8 rounded-full flex items-center justify-center bg-transparent hover:bg-gray-200 border-0 cursor-pointer transition-colors"
                  :aria-label="t('Edit')"
                  @click="customerEditMode = true"
                >
                  <i class="pi pi-pencil text-sm text-gray-500" />
                </button>
                <button
                  class="w-8 h-8 rounded-full flex items-center justify-center bg-transparent hover:bg-gray-200 border-0 cursor-pointer transition-colors"
                  :aria-label="t('Clear client')"
                  @click="onCustomerClear"
                >
                  <i class="pi pi-times text-sm text-gray-500" />
                </button>
              </div>
            </div>

            <!-- Customer edit form (inline) -->
            <div
              v-if="newCheckSelectedCustomer && customerEditMode"
              class="mb-3"
            >
              <SharedCustomerPicker
                ref="customerPickerRef"
                v-model="newCheckSelectedCustomer"
                @select="onCustomerSelect"
                @clear="onCustomerClear"
              />
            </div>

            <!-- Customer picker (when no customer selected) -->
            <div
              v-if="!newCheckSelectedCustomer"
              class="mb-3"
            >
              <SharedCustomerPicker
                ref="customerPickerRef"
                v-model="newCheckSelectedCustomer"
                :show-form="false"
                @select="onCustomerSelect"
                @clear="onCustomerClear"
              />
              <p class="text-xs text-gray-400 mt-2">
                {{ t('Customer is optional. You can skip this step.') }}
              </p>
            </div>

            <!-- Description input (only when no customer selected) -->
            <div
              v-if="!newCheckSelectedCustomer"
              class="flex flex-col gap-1 mb-3"
            >
              <label class="text-sm font-medium text-gray-700">{{ t('Description') }}</label>
              <InputText
                v-model="customLabel"
                :placeholder="t('Order identifier...')"
                class="w-full"
              />
            </div>

            <!-- Open Check Button -->
            <Button
              :label="newCheckSelectedCustomer ? t('Use {name} in new check', { name: newCheckSelectedCustomer.name }) : t('Open Check')"
              icon="pi pi-check"
              severity="success"
              class="w-full"
              @click="openTablelessCheck"
            />
          </div>
        </div>
      </div>
    </Drawer>
  </div>
</template>

<style scoped>
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-shimmer {
  animation: shimmer 2s infinite linear;
}

.animate-fadeInUp {
  animation: fadeInUp 0.4s ease-out forwards;
  opacity: 0;
}
</style>
